import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminWallets,
  fetchAdminWalletTransactions,
  createAdminWalletTransaction,
  clearWalletState,
} from "../../redux/Slices/WalletSlice";
import { toast } from "react-toastify";
import { FaWallet, FaEye, FaPlus, FaSearch } from "react-icons/fa";
import { useTitle } from "../../context/TitleContext";
import { getUserProfile } from "../../redux/Slices/authSlice";

/* ── helpers ─────────────────────────────────────────────── */
const formatCurrency = (val) =>
  val !== undefined && val !== null ? `${Number(val).toLocaleString("ar-EG", { minimumFractionDigits: 2 })} ج.م` : "—";

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleDateString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

/* ── Pagination ──────────────────────────────────────────── */
const PaginationBar = ({ meta, currentPage, onChange }) => {
  if (!meta || meta.last_page <= 1) return null;
  const s = Math.max(1, currentPage - 2), e = Math.min(meta.last_page, currentPage + 2);
  const pages = [];
  for (let i = s; i <= e; i++) {
    pages.push(
      <button key={i} onClick={() => i !== currentPage && onChange(i)}
        style={{ width: 36, height: 36, borderRadius: 8, border: "none", fontWeight: i === currentPage ? 700 : 400,
          background: i === currentPage ? "var(--red-color)" : "#f1f5f9", color: i === currentPage ? "#fff" : "#475569", cursor: "pointer" }}>
        {i}
      </button>
    );
  }
  const nb = (dis, lbl, fn) => (
    <button onClick={fn} disabled={dis} style={{ padding: "0 14px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: dis ? "#cbd5e1" : "#475569", cursor: dis ? "not-allowed" : "pointer", fontWeight: 500 }}>{lbl}</button>
  );
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 24 }}>
      {nb(currentPage === 1, "السابق", () => onChange(currentPage - 1))}
      <div style={{ display: "flex", gap: 4 }}>{pages}</div>
      {nb(currentPage === meta.last_page, "التالي", () => onChange(currentPage + 1))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Main
══════════════════════════════════════════════════════════ */
const AdminWallets = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { adminWallets, adminWalletsMeta, adminTransactions, loading, actionLoading, error, success } = useSelector((s) => s.wallets);
  const { user, profile, userPermissions } = useSelector((s) => s.auth);

  const getLoggedUserRole = () => {
    if (user?.data?.role_name) return user.data.role_name;
    if (user?.data?.role?.name) return user.data.role.name;
    if (user?.data?.role) {
      if (typeof user.data.role === "string") return user.data.role;
      if (user.data.role.name) return user.data.role.name;
    }
    if (user?.role_name) return user.role_name;
    const local = localStorage.getItem("loggedUser");
    if (local) {
      try {
        const parsed = JSON.parse(local);
        const data = parsed?.data || parsed;
        if (data?.role_name) return data.role_name;
        if (data?.role?.name) return data.role.name;
        if (data?.role) {
          if (typeof data.role === "string") return data.role;
          if (data.role.name) return data.role.name;
        }
        if (parsed?.role_name) return parsed.role_name;
      } catch {}
    }
    return null;
  };

  const hasSuperAdminPermission = userPermissions?.some(
    (p) => p.name === "delete-admins" || p.name === "create-roles" || p.name === "delete-roles"
  );

  const isSuperAdmin =
    profile?.role_name === "Super Admin" ||
    getLoggedUserRole() === "Super Admin" ||
    hasSuperAdminPermission;

  const [currentPage, setCurrentPage]             = useState(1);
  const [search, setSearch]                       = useState("");
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin]         = useState(null);

  // Transaction Form States
  const [transType, setTransType]                 = useState("credit");
  const [transAmount, setTransAmount]             = useState("");
  const [transNotes, setTransNotes]               = useState("");
  const [transactionType, setTransactionType]     = useState("manual_adjustment");

  useEffect(() => { setTitle("خزائن خدمة العملاء"); dispatch(getUserProfile()); }, [setTitle, dispatch]);
  useEffect(() => { dispatch(fetchAdminWallets({ page: currentPage, search })); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearWalletState());
      dispatch(fetchAdminWallets({ page: currentPage, search }));
    }
  }, [success, currentPage, search, dispatch]);

  useEffect(() => {
    if (error) { toast.error(error); dispatch(clearWalletState()); }
  }, [error, dispatch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(fetchAdminWallets({ page: 1, search }));
  };

  const resolveAdminId = (wallet) => {
    let adminId = wallet?.owner_id ?? wallet?.admin_id;
    if (adminId && typeof adminId === "object") adminId = adminId.id;
    return adminId ?? wallet?.id;
  };

  const handleViewTransactions = (admin) => {
    setSelectedAdmin(admin);
    const adminId = resolveAdminId(admin);
    dispatch(fetchAdminWalletTransactions({ adminId, page: 1 }));
    setShowTransactionsModal(true);
  };

  const handleAddTransaction = (admin) => {
    if (!isSuperAdmin) { toast.error("عذراً، هذه الصلاحية متاحة للمسؤول فقط (Super Admin)"); return; }
    setSelectedAdmin(admin);
    setTransType("credit");
    setTransAmount("");
    setTransNotes("");
    setTransactionType("manual_adjustment");
    setShowAddTransactionModal(true);
  };

  const submitTransaction = () => {
    const amountVal = parseFloat(transAmount);
    if (isNaN(amountVal) || amountVal <= 0) {
      toast.error("الرجاء إدخال مبلغ صحيح أكبر من الصفر");
      return;
    }
    if (transAmount.toString().includes('.') && transAmount.toString().split('.')[1].length > 2) {
      toast.error("الحد الأقصى للكسور هو رقمين بعد الفاصلة");
      return;
    }
    const adminId = resolveAdminId(selectedAdmin);
    if (!adminId) {
      toast.error("لم يتم تحديد الموظف بشكل صحيح، حاول إعادة تحميل الصفحة.");
      return;
    }
    dispatch(
      createAdminWalletTransaction({
        adminId,
        type: transType,
        transaction_type: transactionType,
        amount: amountVal,
        notes: transNotes,
      })
    ).then((res) => {
      if (!res.error) {
        setShowAddTransactionModal(false);
        dispatch(fetchAdminWalletTransactions({ adminId, page: 1 }));
        dispatch(fetchAdminWallets({ page: currentPage, search }));
      }
    });
  };

  const filteredWallets = adminWallets ? adminWallets.filter((w) => w.owner_type === "admin" || w.admin_id !== null) : [];

  /* overlay style */
  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <FaWallet style={{ color: "var(--red-color)" }} /> خزائن خدمة العملاء
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>
            إجمالي: <strong>{filteredWallets.length}</strong> موظف
          </p>
        </div>
      </div>

      {/* ── Search Bar ──────────────────────────────────── */}
      <form onSubmit={handleSearch}
        style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "10px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, maxWidth: 400 }}>
        <FaSearch style={{ color: "#94a3b8", flexShrink: 0 }} />
        <input type="search" placeholder="بحث باسم الموظف..." value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, border: "none", outline: "none", fontSize: 14, color: "#334155", background: "transparent" }} />
        {search && (
          <button type="button" onClick={() => { setSearch(""); dispatch(fetchAdminWallets({ page: 1, search: "" })); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, padding: 0 }}>✕</button>
        )}
      </form>

      {/* ── Loading ─────────────────────────────────────── */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 72, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────── */}
      {!loading && filteredWallets.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 52 }}>💼</div>
          <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد خزائن موظفين مسجلة</p>
        </div>
      )}

      {/* ── Cards List ──────────────────────────────────── */}
      {!loading && filteredWallets.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filteredWallets.map((wallet, idx) => (
              <div key={wallet.id || idx}
                style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 1px 4px rgba(0,0,0,.04)", transition: "box-shadow .2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(239,68,68,.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)")}
              >
                {/* avatar */}
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#f8fafc", color: "var(--main-color)", border: "1.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, flexShrink: 0 }}>
                  👤
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 3 }}>
                    <strong style={{ fontSize: 15, color: "#0f172a" }}>{wallet.user_name || "—"}</strong>
                    {wallet.status ? (
                      <span style={{ padding: "2px 10px", borderRadius: 20, background: "#d1fae5", color: "#065f46", fontSize: 11, fontWeight: 700 }}>نشط</span>
                    ) : (
                      <span style={{ padding: "2px 10px", borderRadius: 20, background: "#fee2e2", color: "#991b1b", fontSize: 11, fontWeight: 700 }}>غير نشط</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    {/* BUG-71 fix: حسابات الموظفين (الأدمن) لا تملك رقم هاتف في هذا النظام، والقيمة المعروضة هنا هي بريدهم الإلكتروني فعلياً */}
                    📧 {wallet.user_phone || "—"} &nbsp;•&nbsp; 📅 {formatDate(wallet.created_at)}
                  </div>
                </div>

                {/* Balance */}
                <div style={{ flexShrink: 0, textAlign: "center", padding: "0 10px" }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>الرصيد الحالي</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: Number(wallet.balance) > 0 ? "var(--green-color)" : "#64748b" }}>
                    {formatCurrency(wallet.balance)}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ flexShrink: 0, display: "flex", gap: 6 }}>
                  <button onClick={() => handleViewTransactions(wallet)} title="سجل المعاملات"
                    style={{ width: 36, height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "var(--main-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                    <FaEye />
                  </button>
                  {isSuperAdmin && (
                    <button onClick={() => handleAddTransaction(wallet)} title="إضافة معاملة"
                      style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "linear-gradient(135deg,var(--green-color),#059669)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                      <FaPlus />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <PaginationBar meta={adminWalletsMeta} currentPage={currentPage} onChange={setCurrentPage} />
        </>
      )}

      {/* ════════════════════════════════════════════════════
          Modal: سجل المعاملات (Timeline)
      ════════════════════════════════════════════════════ */}
      {showTransactionsModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "90vh", boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            
            {/* Header */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>🧾</span>
                <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>سجل معاملات: {selectedAdmin?.user_name}</span>
              </div>
              <button onClick={() => setShowTransactionsModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>

            {/* Current Balance Row */}
            <div style={{ background: "#f8fafc", padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0" }}>
              <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>الرصيد الحالي في الخزنة</span>
              <strong style={{ fontSize: 16, color: "var(--green-color)" }}>{formatCurrency(selectedAdmin?.balance)}</strong>
            </div>

            {/* Body */}
            <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
              {loading ? (
                <div style={{ textAlign: "center", padding: "30px 0" }}><span className="spinner-border text-danger" /></div>
              ) : !adminTransactions || adminTransactions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>لا توجد معاملات مسجلة بعد</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {adminTransactions.map((tx, idx) => {
                    const isCredit = tx.type === "credit";
                    return (
                      <div key={tx.id || idx}
                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, background: "#fff", border: "1px solid #e2e8f0", borderRight: isCredit ? "4px solid var(--green-color)" : "4px solid var(--red-color)" }}>
                        {/* type circle */}
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: isCredit ? "#d1fae5" : "#fee2e2", color: isCredit ? "#065f46" : "#991b1b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
                          {isCredit ? "⬇" : "⬆"}
                        </div>

                        {/* info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                              {isCredit ? "إيداع مالي" : "سحب مالي"}
                              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400, marginRight: 8 }}>({tx.transaction_type})</span>
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 800, color: isCredit ? "var(--green-color)" : "var(--red-color)" }}>
                              {isCredit ? "+" : "-"} {formatCurrency(tx.amount)}
                            </span>
                          </div>
                          {tx.notes && <div style={{ fontSize: 12, color: "#64748b" }}>📝 {tx.notes}</div>}
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>{formatDate(tx.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "space-between", gap: 10, borderTop: "1px solid #f1f5f9" }}>
              {isSuperAdmin && (
                <button onClick={() => { setShowTransactionsModal(false); handleAddTransaction(selectedAdmin); }}
                  style={{ padding: "8px 18px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,var(--green-color),#059669)", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  <FaPlus size={11} /> إضافة معاملة جديدة
                </button>
              )}
              <button onClick={() => setShowTransactionsModal(false)}
                style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>إغلاق</button>
            </div>

          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          Modal: إضافة معاملة (Super Admin)
      ════════════════════════════════════════════════════ */}
      {showAddTransactionModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            
            {/* Header */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, fontSize: 17, color: "#0f172a" }}>إضافة معاملة — {selectedAdmin?.user_name}</span>
              <button onClick={() => setShowAddTransactionModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>

            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
              
              {/* Type Switcher (Deposit / Withdraw) */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>نوع العملية</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: "#f1f5f9", padding: 4, borderRadius: 10 }}>
                  <button type="button" onClick={() => setTransType("credit")}
                    style={{ padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, background: transType === "credit" ? "var(--green-color)" : "transparent", color: transType === "credit" ? "#fff" : "#475569", transition: "all .2s" }}>
                    📥 إيداع مالي
                  </button>
                  <button type="button" onClick={() => setTransType("debit")}
                    style={{ padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, background: transType === "debit" ? "var(--red-color)" : "transparent", color: transType === "debit" ? "#fff" : "#475569", transition: "all .2s" }}>
                    📤 سحب مالي
                  </button>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>المبلغ (ج.م) <span style={{ color: "var(--red-color)" }}>*</span></label>
                <input type="number" value={transAmount} onChange={(e) => setTransAmount(e.target.value.replace(/-/g, ""))} required min="0.01" step="0.01" placeholder="0.00"
                  style={{ width: "100%", padding: "9px 14px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                  onFocus={(e) => (e.target.style.borderColor = transType === "credit" ? "var(--green-color)" : "var(--red-color)")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")} />
              </div>

              {/* Notes */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>ملاحظات</label>
                <textarea rows={3} placeholder="أي تفاصيل إضافية..." value={transNotes} onChange={(e) => setTransNotes(e.target.value)}
                  style={{ width: "100%", padding: "9px 14px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", boxSizing: "border-box", resize: "vertical" }}
                  onFocus={(e) => (e.target.style.borderColor = transType === "credit" ? "var(--green-color)" : "var(--red-color)")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")} />
              </div>

            </div>

            {/* Footer */}
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setShowAddTransactionModal(false)}
                style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={submitTransaction} disabled={actionLoading}
                style={{ padding: "9px 24px", borderRadius: 9, border: "none", background: transType === "credit" ? "var(--green-color)" : "var(--red-color)", color: "#fff", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {actionLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري التأكيد...</> : "تأكيد العملية"}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminWallets;
